import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Loader2, Pencil, Check, X, Power, Trash2, XCircle } from 'lucide-react';
import {
  createSearchDomain,
  updateSearchDomain,
  deleteSearchDomain,
  type SearchDomain,
} from '../../services/api';
import { useAdminData } from './AdminDataContext';

const toKey = (label: string) =>
  label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

// ─── Add Modal ─────────────────────────────────────────────────────────────
function AddDomainModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const derivedKey = toKey(label.trim());

  const handleSave = async () => {
    const trimmed = label.trim();
    if (!trimmed || !derivedKey) return;
    setSaving(true);
    setError('');
    try {
      await createSearchDomain(derivedKey, trimmed);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Domain</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Add a new talent pool category to the platform.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <label className="block">
            <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Label</span>
            <input
              autoFocus
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              placeholder="e.g. Software Engineering"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 dark:text-gray-500">Key (auto-generated):</span>
            <code className={`text-xs font-mono px-1.5 py-0.5 rounded ${
              derivedKey
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600'
            }`}>
              {derivedKey || '—'}
            </code>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !label.trim() || !derivedKey}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function SearchDomainsSection() {
  const { searchDomains: contextDomains, refreshSearchDomains } = useAdminData();
  const [domains, setDomains] = useState<SearchDomain[]>(contextDomains);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<SearchDomain | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setDomains(contextDomains); }, [contextDomains]);

  useEffect(() => { if (contextDomains.length > 0) setLoading(false); }, [contextDomains]);

  const fetchDomains = useCallback(async () => {
    try {
      await refreshSearchDomains();
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [refreshSearchDomains]);

  const handleToggleActive = async (d: SearchDomain) => {
    try {
      await updateSearchDomain(d.id, { isActive: !d.isActive });
      fetchDomains();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update');
    }
  };

  const handleEditSave = async (id: number) => {
    const label = editLabel.trim();
    if (!label) return;
    try {
      await updateSearchDomain(id, { label });
      setEditingId(null);
      fetchDomains();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSearchDomain(deleteTarget.id);
      setDeleteTarget(null);
      fetchDomains();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const activeCount = domains.filter((d) => d.isActive).length;
  const inactiveCount = domains.length - activeCount;
  const filtered = domains.filter((d) => {
    if (statusFilter === 'active') return d.isActive;
    if (statusFilter === 'inactive') return !d.isActive;
    return true;
  });

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6 transition-opacity duration-300">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Domains</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage the talent pool categories available for search and ingestion.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Domain
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <button
          onClick={() => setStatusFilter(statusFilter === 'all' ? 'all' : 'all')}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'all' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{domains.length}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total</div>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'active' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-green-600">{activeCount}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Active</div>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive')}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'inactive' ? 'border-gray-500 ring-2 ring-gray-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-gray-400">{inactiveCount}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Inactive</div>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
          <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {domains.length === 0 ? 'No managed domains yet.' : 'No domains match the selected filter.'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3">Key</th>
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300">
                      {d.key}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    {editingId === d.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEditSave(d.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="px-2 py-1 text-sm border border-blue-300 dark:border-blue-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => handleEditSave(d.id)}
                          className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition-colors"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-900 dark:text-gray-100">{d.label}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      d.isActive
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
                    }`}>
                      {d.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditingId(d.id); setEditLabel(d.label); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Edit label"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(d)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          d.isActive
                            ? 'text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                        }`}
                        title={d.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(d)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddDomainModal
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); fetchDomains(); }}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Delete Domain</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Delete <strong className="text-gray-700 dark:text-gray-200">{deleteTarget.label}</strong>{' '}
              (<code className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">{deleteTarget.key}</code>)?
              {' '}This removes it from the Add Candidates list and any tenant configs referencing it.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
